import { ReactElement, useState } from 'react';
import { Badge, Popover, Select, SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isHasAccess } from 'features/validateAccess';
import { useApproveTimesheetStatusMutation, useDisapproveTimesheetStatusMutation } from 'entities/timesheet';

import { getTimesheetsApprovePermission } from '../../Timesheets.permissions';
import { EmployeeRow } from '../../TimesheetsTable.types';
import { ConfirmPopoverContent } from '../ConfirmPopoverContent';

import styles from './EmployeeStatusCell.module.scss';

const EmployeeStatusCell = ({ record }: { record: EmployeeRow }): ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const projectId = searchParams.get('projectId') ?? '';

  const canChangeTimesheetStatus = isHasAccess(getTimesheetsApprovePermission(accountId, projectId));

  const isParentProjectClosed = record.parentStatus === 'CLOSED';

  const [open, setOpen] = useState(false);
  const selectValue = record.status === 'APPROVED' ? 'true' : 'false';

  const { mutate: approveTimesheetStatusMutation } = useApproveTimesheetStatusMutation(record.key);
  const { mutate: disapproveTimesheetStatusMutation } = useDisapproveTimesheetStatusMutation(record.key);

  const onChange: SelectProps['onChange'] = (value) => {
    if (!isParentProjectClosed) {
      if (value === 'true') {
        approveTimesheetStatusMutation();
      } else {
        disapproveTimesheetStatusMutation();
      }
    } else {
      setOpen(true);
    }
  };

  const handleOk = (): void => {
    if (selectValue === 'false') {
      approveTimesheetStatusMutation();
    } else {
      disapproveTimesheetStatusMutation();
    }
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen);
  };

  return (
    <div className={styles.employeeStatusCell}>
      <Badge status={selectValue === 'true' ? 'success' : 'default'} />
      {!canChangeTimesheetStatus ? (
        <span className={styles.statusLabel}>
          {record.status === 'APPROVED'
            ? t('timesheetTable.select.approved.label')
            : t('timesheetTable.select.notApproved.label')}
        </span>
      ) : (
        <Popover
          open={open}
          placement="bottom"
          arrow={{ pointAtCenter: true }}
          onOpenChange={handleOpenChange}
          content={
            <ConfirmPopoverContent
              handleOk={handleOk}
              handleCancel={() => setOpen(false)}
              title="Timesheet:assignmentStatusConfirmPopoverContent.text"
            />
          }
          trigger={open ? ['click'] : []}
        >
          <Select value={selectValue} className={styles.select} onChange={onChange}>
            <Select.Option value="false">{t('timesheetTable.select.notApproved.label')}</Select.Option>
            <Select.Option value="true">{t('timesheetTable.select.approved.label')}</Select.Option>
          </Select>
        </Popover>
      )}
    </div>
  );
};

export { EmployeeStatusCell };
